import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOCK_CERTIFICATES, MOCK_INCIDENTS, MOCK_AUDITS, MOCK_VESSELS, PLATFORM_KPIS, resolveClientKey, getClientVessels, getClientCertificates, getClientIncidents, getClientAudits } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Shield, Download, Search, AlertTriangle, TrendingUp, CheckCircle, XCircle } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const ragClass = (status: string) => {
  if (status === "valid") return "bg-green-100 text-green-700";
  if (status === "expiring") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const HSEQCompliance = () => {
  const [certSearch, setCertSearch] = useState("");
  const [certVessel, setCertVessel] = useState("all");
  const [certRag, setCertRag] = useState("all");
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const clientCerts = clientKey ? getClientCertificates(clientKey) : MOCK_CERTIFICATES;
  const clientIncidents = clientKey ? getClientIncidents(clientKey) : MOCK_INCIDENTS;
  const clientAudits = clientKey ? getClientAudits(clientKey) : MOCK_AUDITS;

  const urgentCount = clientCerts.filter(c => c.status !== "valid").length;

  const filteredCerts = clientCerts.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(certSearch.toLowerCase());
    const matchVessel = certVessel === "all" || c.vessel === certVessel;
    const matchRag = certRag === "all" || c.status === certRag;
    return matchSearch && matchVessel && matchRag;
  });

  const incidentTrend = [
    { month: "Oct", nearMiss: 1, equipment: 0, injury: 0 },
    { month: "Nov", nearMiss: 2, equipment: 1, injury: 0 },
    { month: "Dec", nearMiss: 0, equipment: 0, injury: 0 },
    { month: "Jan", nearMiss: 1, equipment: 1, injury: 0 },
    { month: "Feb", nearMiss: 1, equipment: 0, injury: 0 },
    { month: "Mar", nearMiss: 2, equipment: 1, injury: 0 },
  ];

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Safety & Compliance</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">HSEQ & Compliance</h1>
      </div>

      {urgentCount > 0 && (
        <div className="portal-card p-3 mb-4 border-l-4 border-[#DC3545] flex items-center gap-3 bg-red-50">
          <AlertTriangle className="w-5 h-5 text-[#DC3545]" />
          <p className="text-sm text-[#DC3545] font-medium">{urgentCount} certificate(s) require immediate attention</p>
        </div>
      )}

      <Tabs defaultValue="certificates" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="kpis">HSEQ KPIs</TabsTrigger>
          <TabsTrigger value="audits">Audits</TabsTrigger>
        </TabsList>

        <TabsContent value="certificates">
          <div className="portal-card">
            <div className="p-4 flex flex-wrap gap-3 border-b border-[hsl(220,15%,90%)]">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,12%,50%)]" />
                <Input placeholder="Search certificates..." value={certSearch} onChange={e => setCertSearch(e.target.value)} className="pl-9 bg-[hsl(220,20%,97%)]" />
              </div>
              <Select value={certVessel} onValueChange={setCertVessel}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Vessels</SelectItem>{clientVessels.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={certRag} onValueChange={setCertRag}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="valid">Valid</SelectItem><SelectItem value="expiring">Expiring</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[hsl(220,15%,90%)] bg-[hsl(220,20%,97%)]">
                  {["Certificate", "Vessel", "Authority", "Cert No.", "Issue Date", "Expiry", "Status", "Days"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredCerts.map(c => (
                    <tr key={c.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5 font-medium">{c.name}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{c.vessel}</td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{c.issuingAuthority}</td>
                      <td className="px-4 py-2.5 font-mono text-[10px]">{c.certNo}</td>
                      <td className="px-4 py-2.5">{c.issueDate}</td>
                      <td className="px-4 py-2.5">{c.expiryDate}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${ragClass(c.status)}`}>{c.status}</span></td>
                      <td className="px-4 py-2.5 font-bold">{c.daysRemaining <= 0 ? <span className="text-[#DC3545]">{c.daysRemaining}</span> : c.daysRemaining}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="incidents">
          <div className="portal-card">
            <div className="p-4 flex items-center justify-between border-b border-[hsl(220,15%,90%)]">
              <h2 className="font-bold text-[hsl(215,25%,17%)]">Incident & Near-Miss Log</h2>
              <Button onClick={() => toast.success("Generating: Request Safety Report", { description: "Your file will download shortly." })} variant="outline" size="sm">Request Safety Report</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[hsl(220,15%,90%)] bg-[hsl(220,20%,97%)]">
                  {["ID", "Date", "Vessel", "Type", "Severity", "Status", "Days Open"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {clientIncidents.map(inc => (
                    <tr key={inc.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5 font-medium">{inc.incidentId}</td>
                      <td className="px-4 py-2.5">{inc.date}</td>
                      <td className="px-4 py-2.5">{inc.vessel}</td>
                      <td className="px-4 py-2.5">{inc.type}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${inc.severity === "low" ? "bg-green-100 text-green-700" : inc.severity === "medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{inc.severity}</span></td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${inc.status === "closed" ? "bg-gray-100 text-gray-600" : inc.status === "investigating" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{inc.status}</span></td>
                      <td className="px-4 py-2.5">{inc.daysOpen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="kpis">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: "LTI-Free Days", value: PLATFORM_KPIS.ltiFreeDays, color: "#28A745" },
              { label: "TRIR", value: PLATFORM_KPIS.trir.toFixed(2), color: "#28A745" },
              { label: "Near-Miss Rate", value: "0.42", color: "#FFC107" },
              { label: "On-Time %", value: PLATFORM_KPIS.onTimeDeparture + "%", color: "#0066CC" },
              { label: "Fleet Availability", value: PLATFORM_KPIS.fleetAvailability + "%", color: "#0066CC" },
              { label: "Open NCRs", value: PLATFORM_KPIS.openNCRs, color: "#DC3545" },
            ].map(kpi => (
              <div key={kpi.label} className="portal-card p-4 text-center">
                <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-[10px] text-[hsl(215,12%,50%)] mt-1">{kpi.label}</p>
              </div>
            ))}
          </div>
          <div className="portal-card p-4">
            <h3 className="font-bold text-sm mb-3">Incident Categories — 6 Month Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={incidentTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#999" }} />
                <YAxis tick={{ fontSize: 10, fill: "#999" }} />
                <Tooltip />
                <Bar dataKey="nearMiss" name="Near-Miss" fill="#FFC107" radius={[3,3,0,0]} />
                <Bar dataKey="equipment" name="Equipment" fill="#0066CC" radius={[3,3,0,0]} />
                <Bar dataKey="injury" name="Injury" fill="#DC3545" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="audits">
          <div className="portal-card">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[hsl(220,15%,90%)] bg-[hsl(220,20%,97%)]">
                  {["Date", "Type", "Vessel", "Findings", "Status", "Report"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {clientAudits.map(a => (
                    <tr key={a.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                      <td className="px-4 py-2.5">{a.date}</td>
                      <td className="px-4 py-2.5 font-medium">{a.type}</td>
                      <td className="px-4 py-2.5">{a.vessel}</td>
                      <td className="px-4 py-2.5">{a.findings}</td>
                      <td className="px-4 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${a.status === "Closed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span></td>
                      <td className="px-4 py-2.5"><Button onClick={() => toast.success("Action recorded: Action")} variant="ghost" size="sm"><Download className="w-3.5 h-3.5" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default HSEQCompliance;
